import React, { Fragment, ReactNode, useContext, useState } from 'react';
import { AvDialog } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { FailFull as Error } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { iconSize, noop } from '@fabric-ui/utils';
import { Box, ClickAwayListener, Collapse, Divider, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAvContext } from '../../../../context/AvContextProvider';
import { widgetTypeObject } from '../../constants';
import { CustomDashboardContext } from '../../CustomDashboardContext';
import { AllTypeWidgets, BarSubType, LineSubType, PieSubType, TableSubType, TextSubType, TileSubType, WidgetCategory } from '../../types';
import { WidgetTypeIconButton } from '../WidgetTypeIconButton/WidgetTypeIconButton';

export const getKeyByValue = (object, category: string) => Object.entries(object).find(([, val]) => val === category)?.[0];

const SelectWidgetType = ({
  isTabOpen,
  setIsTabOpen,
  onCategoryChange,
}: {
  isTabOpen: boolean;
  setIsTabOpen: (v: boolean) => void;
  onCategoryChange?: (category: WidgetCategory) => void;
}) => {
  const theme = useTheme();
  const { featureFlags } = useAvContext();
  const { newWidget, setNewWidget, initWidget } = useContext(CustomDashboardContext);
  const [drillDownWarning, setDrillDownWarning] = useState({ isOpen: false, onClick: noop });
  const widgetType: WidgetTypeInfo = widgetTypeObject[newWidget.category][newWidget.type];
  const handleClose = () => {
    setDrillDownWarning({ onClick: noop, isOpen: false });
  };

  const onClickWidget = (
    selectedWidgetCategory: WidgetCategory,
    selectedWidgetType: PieSubType | BarSubType | LineSubType | TileSubType | TableSubType
  ) => {
    const defaultNewWidget = widgetTypeObject[selectedWidgetCategory][selectedWidgetType].default;
    const prevDrillDown =
      [WidgetCategory.Table, WidgetCategory.Pie, WidgetCategory.Bar].includes(selectedWidgetCategory) && newWidget.drillDownHierarchy;

    // The logic for handling over-selection is now inside initWidget
    const initializedAndAdjustedWidget = initWidget({
      selectedWidgetType,
      selectedWidgetCategory,
      currentWidget: newWidget, // Pass the current widget as is
    }) as Partial<AllTypeWidgets>;

    const updatedWidget = structuredClone({
      ...defaultNewWidget,
      ...initializedAndAdjustedWidget,
      id: newWidget.id,
      category: selectedWidgetCategory,
      type: selectedWidgetType,
      drillDownHierarchy: prevDrillDown || initializedAndAdjustedWidget.drillDownHierarchy || defaultNewWidget.drillDownHierarchy,
    });

    if (
      (newWidget.category === WidgetCategory.Line || newWidget.category === WidgetCategory.Bar) &&
      newWidget.definition.custom.showDataLabels !== undefined
    ) {
      updatedWidget.definition.custom.showDataLabels = newWidget.definition.custom.showDataLabels;
    }

    onCategoryChange?.(selectedWidgetCategory);
    setNewWidget(updatedWidget);
  };

  const onSelectNewWidget = (category: WidgetCategory, type: PieSubType | BarSubType | LineSubType | TileSubType | TableSubType) => () => {
    if ([WidgetCategory.Table, WidgetCategory.Pie, WidgetCategory.Bar].includes(category) || !newWidget.drillDownHierarchy?.fields.length) {
      onClickWidget(category, type);
    } else {
      setDrillDownWarning({
        isOpen: true,
        onClick: () => {
          onClickWidget(category, type);
          handleClose();
        },
      });
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setIsTabOpen(false)}>
      <Box sx={{ ...flex.col, gap: 2 }}>
        <Box
          onClick={() => setIsTabOpen(!isTabOpen)}
          sx={{
            ...flex.itemsCenter,
            height: 42,
            pl: '8px',
            pr: '12px',
            border: '1px solid var(--color-border-base-secondary)',
            borderRadius: '12px',
          }}>
          <Box sx={{ ...flex.itemsCenter, gap: '7px' }}>
            {widgetType.icon}
            <Typography sx={{ fontSize: 13 }} variant="h6">
              {widgetType.title}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <ArrowDown
            style={{
              ...iconSize(18),
              ...(isTabOpen
                ? {
                    transform: 'rotate(-180deg)',
                    transition: theme.transitions.create(['transform'], { duration: theme.transitions.duration.shorter }),
                  }
                : {}),
            }}
          />
        </Box>
        <Collapse in={isTabOpen} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'fit-content(100%) 0px', columnGap: 5, rowGap: 1 }}>
            {Object.keys(widgetTypeObject)
              .filter(category => category !== WidgetCategory.Text)
              .map(category => (
                <Fragment key={category}>
                  {category !== WidgetCategory.Text && (
                    <Box sx={{ ...flex.itemsStart, pt: 1, fontSize: 12, color: 'var(--color-content-base-secondary)' }}>
                      {getKeyByValue(WidgetCategory, category)}
                    </Box>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', columnGap: 1, rowGap: 1 }}>
                    {Object.keys(widgetTypeObject[category]).map(
                      type =>
                        (!widgetTypeObject[category][type].featureFlag || featureFlags[widgetTypeObject[category][type].featureFlag]) && (
                          <WidgetTypeIconButton
                            onClick={onSelectNewWidget(category as WidgetCategory, type as TableSubType.Table | TableSubType.PivotTable)}
                            selected={newWidget.category === category && newWidget.type === type}
                            type={type as TableSubType.Table | TableSubType.PivotTable}
                            key={type}
                          />
                        )
                    )}
                    {category === WidgetCategory.Tile &&
                      Object.keys(widgetTypeObject[WidgetCategory.Text]).map(type => (
                        <WidgetTypeIconButton
                          onClick={onSelectNewWidget(
                            WidgetCategory.Text,
                            type as BarSubType | LineSubType | PieSubType | TableSubType | TileSubType
                          )}
                          selected={newWidget.category === WidgetCategory.Text && newWidget.type === type}
                          type={type as TextSubType.Text}
                          key={type}
                        />
                      ))}
                  </Box>
                </Fragment>
              ))}
          </Box>
          <Divider sx={{ pt: 2 }} />
        </Collapse>
        <AvDialog
          open={drillDownWarning.isOpen}
          onClose={handleClose}
          title={
            <>
              <Error />
              <div>Existing drill down settings will be deleted</div>
            </>
          }
          footer={
            <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button variant="contained" onClick={drillDownWarning.onClick}>
                Change anyway
              </Button>
            </Box>
          }>
          <div>Changing this widget to line widget type will delete the existing drill down settings.</div>
          <Box sx={{ pb: 5 }}>Would you like to change type anyway?</Box>
        </AvDialog>
      </Box>
    </ClickAwayListener>
  );
};

export default SelectWidgetType;

interface WidgetTypeInfo {
  value: WidgetCategory;
  title: string;
  icon: ReactNode;
}
